import React, { useMemo, useState } from "react";

// REISOR_CLIENT_TRUST_REVIEW_V1
// Driver -> client review after completed order.
// Warm-up canon: thank driver, send to dispetcher/admin, do NOT show public client rating.

const REASONS = [
  { key: "fake_order", label: "Фейковый заказ" },
  { key: "no_response", label: "Не отвечает" },
  { key: "asks_docs_outside_platform", label: "Просит документы вне платформы" },
  { key: "price_mismatch", label: "Цена не соответствует" },
  { key: "suspicious_contact", label: "Подозрительный контакт" },
  { key: "payment_problem", label: "Проблема с оплатой" },
  { key: "fraud", label: "Мошенничество" },
  { key: "other", label: "Другая причина" },
];

function toBearer(token) {
  const s = String(token || "").trim();
  if (!s) return "";
  return /^bearer\s+/i.test(s) ? s : `Bearer ${s}`;
}

function orderIdOf(order) {
  return String(order?.orderId || order?.id || order?._id || "").trim();
}

function routeText(order) {
  const from = String(order?.route?.from || order?.from || "").trim();
  const to = String(order?.route?.to || order?.to || "").trim();
  return [from, to].filter(Boolean).join(" → ");
}

export default function ClientTrustReviewModal({
  open,
  order,
  token,
  onClose,
  onSubmitted,
}) {
  const [rating, setRating] = useState("ok");
  const [reasons, setReasons] = useState([]);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const orderId = orderIdOf(order);
  const route = routeText(order);

  const canSubmit = useMemo(() => {
    if (!orderId || sending) return false;
    if ((rating === "problem" || rating === "suspicious") && reasons.length === 0) return false;
    return true;
  }, [orderId, rating, reasons.length, sending]);

  if (!open || !orderId) return null;

  const toggleReason = (key) => {
    setReasons((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      if (arr.includes(key)) return arr.filter((x) => x !== key);
      return [...arr, key].slice(0, 8);
    });
  };

  const submit = async () => {
    if (!canSubmit) {
      setError("Выберите причину проблемы");
      return;
    }

    setSending(true);
    setError("");

    try {
      const headers = { "Content-Type": "application/json" };
      const bearer = toBearer(token);
      if (bearer) headers.Authorization = bearer;

      const res = await fetch(`/api/client-trust/order/${encodeURIComponent(orderId)}/submit`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ rating, reasons, comment }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) {
        const code = String(data?.error || "").trim();
        if (code === "ORDER_NOT_FOUND_OR_NOT_ELIGIBLE") {
          onClose?.();
          return;
        }
        throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
      }

      onSubmitted?.(data?.message || "Спасибо. Ваша оценка помогает REISOR защищать водителей от фейковых заказов.");
    } catch (e) {
      setError(String(e?.message || e || "Ошибка отправки"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10080] bg-black/70 flex items-end sm:items-center justify-center px-3 pt-3"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 72px)" }}
    >
      <div
        className="w-full max-w-md rounded-t-3xl sm:rounded-3xl border border-slate-700 bg-slate-900 text-white shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: "calc(100dvh - max(env(safe-area-inset-bottom, 0px), 72px) - env(safe-area-inset-top, 0px) - 14px)" }}

        data-reisor-theme-surface="driver-trust-review">
        <div className="px-4 py-4 border-b border-slate-700 shrink-0">
          <div className="text-xl font-black">Оцените заказчика</div>
          <div className="mt-1 text-sm text-slate-300">
            Заказ №{orderId}
            {route ? <span> · {route}</span> : null}
          </div>
        </div>

        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          <div className="grid gap-2">
            {[
              ["ok", "Всё хорошо", "Можно быстро отправить положительную оценку"],
              ["problem", "Были проблемы", "Условия, связь, оплата или цена"],
              ["suspicious", "Подозрительно / мошенничество", "Фейк, сбор данных, подозрительные контакты"],
            ].map(([key, title, desc]) => {
              const active = rating === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setRating(key);
                    if (key === "ok") setReasons([]);
                    setError("");
                  }}
                  className={`w-full rounded-2xl border px-4 py-3 text-left ${
                    active
                      ? "border-emerald-400 bg-emerald-500/15"
                      : "border-slate-700 bg-slate-800/50"
                  }`}
                >
                  <div className="text-base font-black">{title}</div>
                  <div className="mt-1 text-xs text-slate-300">{desc}</div>
                </button>
              );
            })}
          </div>

          {rating !== "ok" ? (
            <div className="mt-4">
              <div className="mb-2 text-sm font-black text-slate-100">Причины</div>
              <div className="grid gap-2">
                {REASONS.map((r) => {
                  const checked = reasons.includes(r.key);
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => toggleReason(r.key)}
                      className={`w-full rounded-2xl border px-4 py-3 text-left text-sm font-bold ${
                        checked
                          ? "border-blue-400 bg-blue-500/15"
                          : "border-slate-700 bg-slate-800/40"
                      }`}
                    >
                      {checked ? "✓ " : ""}{r.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="mt-4">
            <div className="mb-2 text-sm font-black text-slate-100">
              Комментарий для REISOR
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 700))}
              placeholder="Необязательно. Это увидит только dispetcher/admin."
              className="w-full min-h-[92px] rounded-2xl border border-slate-700 bg-slate-800/60 px-4 py-3 text-sm text-white outline-none"
            />
            <div className="mt-1 text-right text-xs text-slate-400">{comment.length}/700</div>
          </div>

          {error ? (
            <div className="mt-3 rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-100">
              {error}
            </div>
          ) : null}

          <div className="mt-3 rounded-2xl border border-blue-400/20 bg-blue-500/10 px-4 py-3 text-xs leading-relaxed text-blue-100">
            Водителям рейтинг клиента пока не показывается. Оценка нужна REISOR, чтобы находить фейковые заказы и защищать водителей.
          </div>
        </div>

        <div
          className="border-t border-slate-700 p-3 flex gap-2 shrink-0"
          style={{ paddingBottom: "14px" }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="flex-1 rounded-2xl border border-slate-700 bg-slate-800 px-4 py-3 text-sm font-black text-slate-100"
          >
            Позже
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className={`flex-[1.4] rounded-2xl px-4 py-3 text-sm font-black ${
              canSubmit
                ? "bg-emerald-600 text-white"
                : "bg-slate-700 text-slate-400"
            }`}
          >
            {sending ? "Отправляем..." : "Отправить"}
          </button>
        </div>
      </div>
    </div>
  );
}
